import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button, Card, CardBody, CardHeader, Spinner } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';
import CreatorCard from '../components/CreatorCard';
import SEOHead from '../components/SEOHead';
import { productService } from '../services/productService';
import { Creator } from '../types/creators';

const Browse: React.FC = () => {
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => productService.getCategories(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: materials, isLoading: materialsLoading } = useQuery({
    queryKey: ['materials'],
    queryFn: () => productService.getMaterials(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: creators, isLoading: creatorsLoading } = useQuery<Creator[]>({
    queryKey: ['creators'],
    queryFn: () => productService.getCreators(),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <SEOHead
        title='Browse Miniatures'
        description='Explore our collection by category, material, or creator. Start browsing premium 3D printed miniatures for tabletop gaming and painting.'
        url={'https://cgminis.co.uk/browse'}
        type='website'
      />

      <div className='mb-8'>
        <h1 className='text-3xl lg:text-4xl font-bold text-gray-900 mb-2'>
          Browse Miniatures
        </h1>
        <p className='text-gray-600 text-lg'>
          Discover by category, material, or licensed creator.
        </p>
      </div>

      {/* Licensed Creators (moved to top) */}
      <section className='mb-12'>
        <div className='flex items-center justify-between mb-4'>
          <h2 className='text-2xl font-semibold text-gray-900'>
            Licensed Creators
          </h2>
          <Button
            as={Link}
            to='/search'
            variant='light'
            endContent={<ChevronRightIcon className='w-4 h-4' />}
          >
            View all
          </Button>
        </div>
        {creatorsLoading ? (
          <div className='flex items-center gap-2 text-gray-500'>
            <Spinner size='sm' /> Loading creators...
          </div>
        ) : creators && creators.length > 0 ? (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {creators.slice(0, 6).map((creator: Creator) => (
              <CreatorCard key={creator.id} creator={creator} compact />
            ))}
          </div>
        ) : (
          <Card>
            <CardBody>No creators available at the moment.</CardBody>
          </Card>
        )}
      </section>

      {/* Categories */}
      <section className='mb-12'>
        <div className='flex items-center justify-between mb-4'>
          <h2 className='text-2xl font-semibold text-gray-900'>Categories</h2>
          <Button
            as={Link}
            to='/search'
            variant='light'
            endContent={<ChevronRightIcon className='w-4 h-4' />}
          >
            View all
          </Button>
        </div>
        {categoriesLoading ? (
          <div className='flex items-center gap-2 text-gray-500'>
            <Spinner size='sm' /> Loading categories...
          </div>
        ) : (
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'>
            {(categories || []).slice(0, 8).map((cat: any) => (
              <Link
                key={cat.name}
                to={`/search?category=${encodeURIComponent(cat.name)}`}
              >
                <Card className='hover:shadow-md transition-shadow'>
                  <CardBody className='py-5 text-center font-medium'>
                    {cat.name}
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Materials (moved near bottom) */}
      <section className='mb-12'>
        <div className='flex items-center justify-between mb-4'>
          <h2 className='text-2xl font-semibold text-gray-900'>Materials</h2>
          <Button
            as={Link}
            to='/search'
            variant='light'
            endContent={<ChevronRightIcon className='w-4 h-4' />}
          >
            Explore
          </Button>
        </div>
        {materialsLoading ? (
          <div className='flex items-center gap-2 text-gray-500'>
            <Spinner size='sm' /> Loading materials...
          </div>
        ) : (
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'>
            {(materials || []).slice(0, 12).map((mat: any) => (
              <Link
                key={mat.name}
                to={`/search?material=${encodeURIComponent(mat.name)}`}
              >
                <Card className='hover:shadow-md transition-shadow'>
                  <CardBody className='py-4 text-center'>{mat.name}</CardBody>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Quick links */}
      <section className='mb-4'>
        <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
          Quick Links
        </h2>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <Link to='/search?isFeatured=true'>
            <Card className='hover:shadow-md transition-shadow'>
              <CardHeader>
                <div className='font-semibold'>Featured</div>
              </CardHeader>
              <CardBody className='text-gray-600'>
                Curated miniatures we love
              </CardBody>
            </Card>
          </Link>
          <Link to='/search?sortBy=name&sortOrder=asc'>
            <Card className='hover:shadow-md transition-shadow'>
              <CardHeader>
                <div className='font-semibold'>All Miniatures</div>
              </CardHeader>
              <CardBody className='text-gray-600'>
                Browse the full catalog
              </CardBody>
            </Card>
          </Link>
          <Link to='/search'>
            <Card className='hover:shadow-md transition-shadow'>
              <CardHeader>
                <div className='font-semibold'>Advanced Filters</div>
              </CardHeader>
              <CardBody className='text-gray-600'>
                Refine by category, creator, material, and price
              </CardBody>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Browse;
